import { AssetTypeEnum } from '@/shared/types/types.generated';
import { getStableUniqueIds } from '@/shared/utils/get-stable-unique-ids';

import { api } from '../../_generated/api/assets-api.generated';

type TReadLinkedTagsForAssetsQueryVariables = {
  assetIds: string[];
  assetType: AssetTypeEnum;
};

type TLinkedTagName = {
  name: string;
};

type TReadLinkedTagsForAssetsRawResponse = Record<
  string,
  {
    items?: Array<TLinkedTagName | null | undefined>;
  }
>;

type TReadLinkedTagsForAssetsQuery = {
  readLinkedTagsForAssets: Array<{
    assetId: string;
    tags: TLinkedTagName[];
  }>;
};

export const MAX_LINKED_TAGS_BATCH_SIZE = 100;

const getBoundedStableAssetIds = (assetIds: string[]) => {
  const stableAssetIds = getStableUniqueIds(assetIds);

  if (stableAssetIds.length > MAX_LINKED_TAGS_BATCH_SIZE) {
    throw new Error(
      `ReadLinkedTagsForAssets supports up to ${MAX_LINKED_TAGS_BATCH_SIZE} asset IDs`,
    );
  }

  return stableAssetIds;
};

const buildReadLinkedTagsForAssetsDocument = (assetIds: string[]) => {
  const variableDefinitions = assetIds
    .map((_, index) => `$assetId${index}: String!`)
    .join(', ');
  const fields = assetIds
    .map(
      (_, index) => `
    asset${index}: readLinkedTagsForAsset(assetId: $assetId${index}, assetType: $assetType) {
      items {
        name
      }
    }`,
    )
    .join('');

  return `
query ReadLinkedTagsForAssets($assetType: AssetTypeEnum!, ${variableDefinitions}) {
${fields}
}`;
};

const buildReadLinkedTagsForAssetsVariables = ({
  assetIds,
  assetType,
}: TReadLinkedTagsForAssetsQueryVariables) => {
  return getBoundedStableAssetIds(assetIds).reduce<Record<string, string>>(
    (variables, assetId, index) => {
      variables[`assetId${index}`] = assetId;
      return variables;
    },
    { assetType },
  );
};

const enhancedTagsApi = api.enhanceEndpoints({
  addTagTypes: ['Tag'],
  endpoints: {
    ReadAllTags: {
      providesTags: (result) => {
        if (!result?.readAllTags?.items) {
          return [{ type: 'Tag' as const, id: 'LIST' }];
        }

        const tags = result?.readAllTags.items;

        return [
          ...tags.map(({ name }) => ({
            type: 'Tag' as const,
            id: name,
          })),
          { type: 'Tag' as const, id: 'LIST' },
        ];
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Refetch if filter changes or if requesting next page
        return currentArg !== previousArg;
      },
    },
    CreateTag: {
      async onQueryStarted(newTag, { dispatch, queryFulfilled, getState }) {
        const currentData = tagsApi.endpoints.ReadAllTags.select()(getState());

        if (currentData?.data?.readAllTags?.items) {
          const patchResult = dispatch(
            tagsApi.util.updateQueryData('ReadAllTags', undefined, (draft) => {
              draft.readAllTags.items.push({
                ...newTag.tag,
              });
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult?.undo();
          }
        }
      },
    },
    DefineAssetTags: {
      invalidatesTags: (_, __, args) => [
        {
          type: 'Tag' as const,
          id: `${args.assetType}:${args.assetId}`,
        },
      ],
    },
    ReadLinkedTagsForAsset: {
      providesTags: (_, __, args) => [
        {
          type: 'Tag' as const,
          id: `${args.assetType}:${args.assetId}`,
        },
      ],
    },
    ReadAllFullTags: {
      providesTags: (result) => {
        if (!result?.readAllTags?.items) {
          return [{ type: 'Tag' as const, id: 'LIST' }];
        }

        const tags = result?.readAllTags.items;

        return [
          ...tags.map(({ name }) => ({
            type: 'Tag' as const,
            id: name,
          })),
          { type: 'Tag' as const, id: 'LIST' },
        ];
      },
    },
    DeleteTag: {
      invalidatesTags: (_, __, args) => [
        { type: 'Tag' as const, id: args.name },
        { type: 'Tag' as const, id: 'LIST' },
      ],
    },
  },
});

export const tagsApi = enhancedTagsApi.injectEndpoints({
  endpoints: (build) => ({
    ReadLinkedTagsForAssets: build.query<
      TReadLinkedTagsForAssetsQuery,
      TReadLinkedTagsForAssetsQueryVariables
    >({
      query: (variables) => {
        const assetIds = getBoundedStableAssetIds(variables.assetIds);

        return {
          document: buildReadLinkedTagsForAssetsDocument(assetIds),
          variables: buildReadLinkedTagsForAssetsVariables({
            ...variables,
            assetIds,
          }),
        };
      },
      transformResponse: (
        response: TReadLinkedTagsForAssetsRawResponse,
        _meta,
        args,
      ) => {
        const assetIds = getBoundedStableAssetIds(args.assetIds);

        return {
          readLinkedTagsForAssets: assetIds.map((assetId, index) => ({
            assetId,
            tags:
              response[`asset${index}`]?.items?.filter(
                (tag): tag is TLinkedTagName => Boolean(tag),
              ) ?? [],
          })),
        };
      },
      providesTags: (_, __, args) =>
        getBoundedStableAssetIds(args.assetIds).map((assetId) => ({
          type: 'Tag' as const,
          id: `${args.assetType}:${assetId}`,
        })),
    }),
  }),
});

export const {
  useCreateTagMutation,
  useReadAllTagsQuery,
  useReadAllFullTagsQuery,
  useDefineAssetTagsMutation,
  useReadLinkedTagsForAssetQuery,
  useLazyReadLinkedTagsForAssetQuery,
  useReadLinkedTagsForAssetsQuery,
  useDeleteTagMutation,
} = tagsApi;
